import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
    Loader2, Save, Eye, Palette, Type, Layout, Settings2,
    GripVertical, ChevronDown, ChevronRight, FileText, SquarePen
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ColorInput = ({ label, value, onChange, testId }) => (
    <div className="flex items-center gap-3">
        <div className="relative">
            <input
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-10 h-10 rounded cursor-pointer border border-brand-gray-200 p-0.5"
                data-testid={testId}
            />
        </div>
        <div className="flex-1 min-w-0">
            <span className="text-sm text-brand-charcoal">{label}</span>
            <span className="text-xs text-brand-gray-400 ml-2 uppercase">{value}</span>
        </div>
    </div>
);

const NumberInput = ({ label, value, onChange, min, max, step, suffix, testId }) => (
    <div className="flex items-center justify-between gap-3">
        <Label className="text-sm text-brand-charcoal whitespace-nowrap">{label}</Label>
        <div className="flex items-center gap-1">
            <Input
                type="number"
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                min={min}
                max={max}
                step={step || 1}
                className="h-9 w-20 text-right text-sm"
                data-testid={testId}
            />
            {suffix && <span className="text-xs text-brand-gray-400 w-6">{suffix}</span>}
        </div>
    </div>
);

const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Card className="border border-brand-gray-200 shadow-sm">
            <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-brand-gray-50 transition-colors"
                onClick={() => setOpen(!open)}
            >
                {Icon && <Icon className="h-4 w-4 text-brand-lime flex-shrink-0" />}
                <span className="text-sm font-bold text-brand-charcoal flex-1">{title}</span>
                {open ? <ChevronDown className="h-4 w-4 text-brand-gray-400" /> : <ChevronRight className="h-4 w-4 text-brand-gray-400" />}
            </div>
            {open && <CardContent className="pt-0 pb-4 px-4 space-y-3 border-t border-brand-gray-100">{children}</CardContent>}
        </Card>
    );
};

const ReportDesigner = () => {
    const { token } = useAuth();
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [dragIdx, setDragIdx] = useState(null);

    useEffect(() => { fetchTemplate(); }, []);

    const fetchTemplate = async () => {
        try {
            const res = await axios.get(`${API}/admin/pdf-template`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTemplate(res.data);
        } catch { toast.error('Failed to load template'); }
        finally { setLoading(false); }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await axios.put(`${API}/admin/pdf-template`, template, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTemplate(res.data);
            toast.success('Template saved successfully');
        } catch { toast.error('Failed to save template'); }
        finally { setSaving(false); }
    };

    const handlePreview = async () => {
        // Save first, then preview
        setPreviewing(true);
        try {
            await axios.put(`${API}/admin/pdf-template`, template, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const res = await axios.get(`${API}/admin/pdf-template/preview`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            setPreviewUrl(url);
        } catch { toast.error('Failed to generate preview'); }
        finally { setPreviewing(false); }
    };

    // Template helpers
    const updateColor = useCallback((key, val) => {
        setTemplate(prev => ({ ...prev, colors: { ...prev.colors, [key]: val } }));
    }, []);

    const updateFontSize = useCallback((key, val) => {
        setTemplate(prev => ({ ...prev, font_sizes: { ...prev.font_sizes, [key]: val } }));
    }, []);

    const updateMargin = useCallback((key, val) => {
        setTemplate(prev => ({ ...prev, margins: { ...prev.margins, [key]: val } }));
    }, []);

    const updateHeader = useCallback((key, val) => {
        setTemplate(prev => ({ ...prev, header: { ...prev.header, [key]: val } }));
    }, []);

    const updateTerms = useCallback((key, val) => {
        setTemplate(prev => ({ ...prev, terms: { ...prev.terms, [key]: val } }));
    }, []);

    const updateBankDetails = useCallback((key, val) => {
        setTemplate(prev => ({ ...prev, bank_details: { ...(prev.bank_details || {}), [key]: val } }));
    }, []);

    const toggleSection = useCallback((sectionId) => {
        setTemplate(prev => ({
            ...prev,
            sections: prev.sections.map(s =>
                s.id === sectionId ? { ...s, enabled: !s.enabled } : s
            )
        }));
    }, []);

    const updateSectionTitle = useCallback((sectionId, title) => {
        setTemplate(prev => ({
            ...prev,
            sections: prev.sections.map(s =>
                s.id === sectionId ? { ...s, title } : s
            )
        }));
    }, []);

    // Drag-and-drop for section reordering
    const handleDragStart = (idx) => setDragIdx(idx);
    const handleDragOver = (e, idx) => {
        e.preventDefault();
        if (dragIdx === null || dragIdx === idx) return;
        setTemplate(prev => {
            const newSections = [...prev.sections];
            const [moved] = newSections.splice(dragIdx, 1);
            newSections.splice(idx, 0, moved);
            setDragIdx(idx);
            return { ...prev, sections: newSections };
        });
    };
    const handleDragEnd = () => setDragIdx(null);

    if (loading || !template) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="h-8 w-8 animate-spin text-brand-lime" />
            </div>
        );
    }

    const { colors: tc, font_sizes: tf, margins: tm, sections, header: th, terms: tt, bank_details: tbd } = template;

    return (
        <div className="animate-fade-in" data-testid="report-designer-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl font-bold font-heading text-brand-charcoal">Report Designer</h1>
                    <p className="text-brand-gray-500 mt-1">Customise how your PDF quotes look</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={handlePreview}
                        disabled={previewing}
                        className="h-10"
                        data-testid="preview-btn"
                    >
                        {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                        Preview
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        className="h-10 bg-[#7AC943] hover:bg-[#68B035] text-black font-bold"
                        data-testid="save-template-btn"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        Save Template
                    </Button>
                </div>
            </div>

            {/* Main layout: Editor + Preview */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                {/* Left: Editor panels */}
                <div className="lg:col-span-2 space-y-3">
                    {/* Colours */}
                    <CollapsibleSection title="Colours" icon={Palette} defaultOpen={true}>
                        <ColorInput label="Primary / Headings" value={tc.primary} onChange={(v) => updateColor('primary', v)} testId="color-primary" />
                        <ColorInput label="Heading Text" value={tc.heading_text} onChange={(v) => updateColor('heading_text', v)} testId="color-heading" />
                        <ColorInput label="Body Text" value={tc.body_text} onChange={(v) => updateColor('body_text', v)} testId="color-body" />
                        <ColorInput label="Muted Text" value={tc.muted_text} onChange={(v) => updateColor('muted_text', v)} testId="color-muted" />
                        <div className="pt-2 border-t border-brand-gray-100">
                            <p className="text-xs font-bold text-brand-gray-500 uppercase tracking-wide mb-2">Table</p>
                            <div className="space-y-3">
                                <ColorInput label="Table Header Background" value={tc.table_header_bg} onChange={(v) => updateColor('table_header_bg', v)} testId="color-table-bg" />
                                <ColorInput label="Table Header Text" value={tc.table_header_text} onChange={(v) => updateColor('table_header_text', v)} testId="color-table-text" />
                                <ColorInput label="Table Border" value={tc.table_border} onChange={(v) => updateColor('table_border', v)} testId="color-table-border" />
                            </div>
                        </div>
                        <ColorInput label="Footer Text" value={tc.footer_text} onChange={(v) => updateColor('footer_text', v)} testId="color-footer" />
                    </CollapsibleSection>

                    {/* Font Sizes */}
                    <CollapsibleSection title="Font Sizes" icon={Type}>
                        <NumberInput label="Company Name" value={tf.company_name} onChange={(v) => updateFontSize('company_name', v)} min={12} max={40} suffix="pt" testId="fs-company" />
                        <NumberInput label="Tagline" value={tf.tagline} onChange={(v) => updateFontSize('tagline', v)} min={8} max={24} suffix="pt" testId="fs-tagline" />
                        <NumberInput label="Section Heading" value={tf.section_heading} onChange={(v) => updateFontSize('section_heading', v)} min={10} max={24} suffix="pt" testId="fs-heading" />
                        <NumberInput label="Body Text" value={tf.body} onChange={(v) => updateFontSize('body', v)} min={8} max={16} suffix="pt" testId="fs-body" />
                        <NumberInput label="Table Header" value={tf.table_header} onChange={(v) => updateFontSize('table_header', v)} min={8} max={16} suffix="pt" testId="fs-table-header" />
                        <NumberInput label="Table Body" value={tf.table_body} onChange={(v) => updateFontSize('table_body', v)} min={8} max={14} suffix="pt" testId="fs-table-body" />
                        <NumberInput label="Total Value" value={tf.total_value} onChange={(v) => updateFontSize('total_value', v)} min={10} max={24} suffix="pt" testId="fs-total" />
                        <NumberInput label="Footer" value={tf.footer} onChange={(v) => updateFontSize('footer', v)} min={6} max={14} suffix="pt" testId="fs-footer" />
                        <div className="pt-2 border-t border-brand-gray-100">
                            <p className="text-xs font-bold text-brand-gray-500 uppercase tracking-wide mb-2">Supplemental (Terms / Bank Details)</p>
                            <div className="space-y-3">
                                <NumberInput label="Supplemental Heading" value={tf.supplemental_heading || 11} onChange={(v) => updateFontSize('supplemental_heading', v)} min={7} max={16} suffix="pt" testId="fs-supp-heading" />
                                <NumberInput label="Supplemental Body Text" value={tf.supplemental_body || 9} onChange={(v) => updateFontSize('supplemental_body', v)} min={6} max={14} suffix="pt" testId="fs-supp-body" />
                            </div>
                        </div>
                    </CollapsibleSection>

                    {/* Page Layout */}
                    <CollapsibleSection title="Page Layout" icon={Layout}>
                        <p className="text-xs text-brand-gray-500">Margins (cm)</p>
                        <div className="grid grid-cols-2 gap-3">
                            <NumberInput label="Top" value={tm.top} onChange={(v) => updateMargin('top', v)} min={0.5} max={5} step={0.25} suffix="cm" testId="margin-top" />
                            <NumberInput label="Bottom" value={tm.bottom} onChange={(v) => updateMargin('bottom', v)} min={0.5} max={5} step={0.25} suffix="cm" testId="margin-bottom" />
                            <NumberInput label="Left" value={tm.left} onChange={(v) => updateMargin('left', v)} min={0.5} max={5} step={0.25} suffix="cm" testId="margin-left" />
                            <NumberInput label="Right" value={tm.right} onChange={(v) => updateMargin('right', v)} min={0.5} max={5} step={0.25} suffix="cm" testId="margin-right" />
                        </div>
                    </CollapsibleSection>

                    {/* Logo / Header Settings */}
                    <CollapsibleSection title="Logo Settings" icon={FileText}>
                        <div className="flex items-center justify-between">
                            <Label className="text-sm">Show Logo</Label>
                            <Switch checked={th.show_logo} onCheckedChange={(v) => updateHeader('show_logo', v)} data-testid="toggle-show-logo" />
                        </div>
                        {th.show_logo && (
                            <>
                                <div className="space-y-1">
                                    <Label className="text-xs text-brand-gray-500">Logo Position</Label>
                                    <div className="flex gap-2">
                                        {['left', 'center', 'right'].map(pos => (
                                            <Button
                                                key={pos}
                                                variant={th.logo_position === pos ? 'default' : 'outline'}
                                                size="sm"
                                                className={`flex-1 text-xs capitalize ${th.logo_position === pos ? 'bg-[#7AC943] hover:bg-[#68B035] text-black' : ''}`}
                                                onClick={() => updateHeader('logo_position', pos)}
                                                data-testid={`logo-pos-${pos}`}
                                            >
                                                {pos}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                                <NumberInput label="Max Width" value={th.logo_width} onChange={(v) => updateHeader('logo_width', v)} min={1} max={6} step={0.25} suffix="in" testId="logo-width" />
                            </>
                        )}
                    </CollapsibleSection>

                    {/* Section Ordering & Visibility */}
                    <CollapsibleSection title="Sections" icon={Settings2} defaultOpen={true}>
                        <p className="text-xs text-brand-gray-500 mb-1">Drag to reorder. Toggle to show/hide.</p>
                        <div className="space-y-1">
                            {sections.map((section, idx) => (
                                <div
                                    key={section.id}
                                    draggable
                                    onDragStart={() => handleDragStart(idx)}
                                    onDragOver={(e) => handleDragOver(e, idx)}
                                    onDragEnd={handleDragEnd}
                                    className={`flex items-center gap-2 p-2 rounded border transition-colors ${
                                        dragIdx === idx ? 'border-brand-lime bg-brand-lime/5' : 'border-brand-gray-200 bg-white'
                                    } ${!section.enabled ? 'opacity-50' : ''}`}
                                    data-testid={`section-${section.id}`}
                                >
                                    <GripVertical className="h-4 w-4 text-brand-gray-300 cursor-grab flex-shrink-0" />
                                    <span className="text-sm flex-1 text-brand-charcoal">{section.label}</span>
                                    {section.title !== undefined && section.id !== 'header' && section.id !== 'quote_info' && section.id !== 'totals' && section.id !== 'footer' && (
                                        <Input
                                            value={section.title || ''}
                                            onChange={(e) => updateSectionTitle(section.id, e.target.value)}
                                            className="h-7 w-28 text-xs"
                                            placeholder="Section title"
                                            onClick={(e) => e.stopPropagation()}
                                            data-testid={`section-title-${section.id}`}
                                        />
                                    )}
                                    <Switch
                                        checked={section.enabled}
                                        onCheckedChange={() => toggleSection(section.id)}
                                        className="flex-shrink-0"
                                        data-testid={`toggle-${section.id}`}
                                    />
                                </div>
                            ))}
                        </div>
                    </CollapsibleSection>

                    {/* Terms & Conditions */}
                    <CollapsibleSection title="Terms & Conditions" icon={SquarePen}>
                        <p className="text-xs text-brand-gray-500 mb-1">
                            Enable the "Terms & Conditions" section above, then add your content here.
                        </p>
                        <textarea
                            value={tt.content || ''}
                            onChange={(e) => updateTerms('content', e.target.value)}
                            placeholder="1. This quotation is valid for 30 days...&#10;2. Prices are subject to change..."
                            className="w-full min-h-[120px] px-3 py-2 bg-background border border-input rounded-lg text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                            data-testid="terms-content"
                        />
                    </CollapsibleSection>

                    {/* Bank Details */}
                    <CollapsibleSection title="Bank Details" icon={SquarePen}>
                        <p className="text-xs text-brand-gray-500 mb-1">
                            Enable the "Bank Details" section above, then add your bank information here.
                        </p>
                        <textarea
                            value={(tbd || {}).content || ''}
                            onChange={(e) => updateBankDetails('content', e.target.value)}
                            placeholder="Bank: HSBC&#10;Account Name: Advanced Dynamics Ltd&#10;Sort Code: 12-34-56&#10;Account No: 12345678"
                            className="w-full min-h-[120px] px-3 py-2 bg-background border border-input rounded-lg text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                            data-testid="bank-details-content"
                        />
                    </CollapsibleSection>
                </div>

                {/* Right: PDF Preview */}
                <div className="lg:col-span-3">
                    <Card className="border-0 shadow-lg sticky top-20">
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-base font-bold font-heading flex items-center gap-2">
                                    <Eye className="h-4 w-4 text-brand-lime" /> PDF Preview
                                </CardTitle>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handlePreview}
                                    disabled={previewing}
                                    data-testid="refresh-preview-btn"
                                >
                                    {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
                                    {previewing ? 'Generating...' : 'Refresh'}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {previewUrl ? (
                                <div className="border border-brand-gray-200 rounded bg-brand-gray-50">
                                    <embed
                                        src={previewUrl + '#toolbar=1&view=FitH'}
                                        type="application/pdf"
                                        className="w-full rounded"
                                        style={{ height: '75vh', minHeight: '500px' }}
                                        data-testid="pdf-preview-embed"
                                    />
                                    <div className="flex justify-center py-2 border-t border-brand-gray-200">
                                        <a
                                            href={previewUrl}
                                            download="quote-preview.pdf"
                                            className="text-sm text-brand-lime hover:underline flex items-center gap-1"
                                            data-testid="download-preview-link"
                                        >
                                            <FileText className="h-3 w-3" /> Download Preview PDF
                                        </a>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-brand-gray-300 rounded bg-brand-gray-50">
                                    <FileText className="h-12 w-12 text-brand-gray-300 mb-3" />
                                    <p className="text-brand-gray-500 text-sm font-medium">No preview generated yet</p>
                                    <p className="text-brand-gray-400 text-xs mt-1">Click "Preview" or "Refresh" to see your PDF</p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="mt-4"
                                        onClick={handlePreview}
                                        disabled={previewing}
                                        data-testid="initial-preview-btn"
                                    >
                                        {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                                        Generate Preview
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ReportDesigner;
